/**
 * `<validated-form>` (sections/b2b-application.liquid): wraps a plain Shopify form that already
 * works without JS (the browser checks `required` and `type="email"` itself). With JS the form
 * shows the design's own messages instead of the browser's bubbles:
 *
 * - Checks on submit; after the first try each field re-checks while the shopper corrects it.
 * - A field's message is the `[data-error]` element its `aria-describedby` points to.
 * - The first invalid field gets focus; the button is never disabled, so it keeps focus.
 * - A valid submit goes ahead as a normal POST, with the wait shown on the button at once
 *   (`aria-busy`, shared .button spinner). A second click does nothing.
 * - Shopify puts its hCaptcha check in front of contact forms: it holds the submit, may show a
 *   challenge and posts the form itself. When the shopper closes the challenge the page stays,
 *   so the wait ends after a few seconds and the button works again.
 */

const RELEASE_AFTER = 6000;

class ValidatedForm extends HTMLElement {
  #abort = null;
  #tried = false;
  #sending = false;
  #release = 0;

  connectedCallback() {
    const form = this.querySelector("form");
    if (!form) return;
    form.noValidate = true;
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    form.addEventListener("submit", (event) => this.#onSubmit(event, form), { signal });
    form.addEventListener("input", (event) => {
      if (this.#tried && event.target.matches?.("[required]")) this.#check(event.target);
    }, { signal });
    // Back from the next page (bfcache): the button must not stay busy.
    window.addEventListener("pageshow", () => this.#done(form), { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    clearTimeout(this.#release);
  }

  #done(form) {
    clearTimeout(this.#release);
    this.#sending = false;
    form.querySelector("[data-submit]")?.removeAttribute("aria-busy");
  }

  #errorFor(field) {
    const ids = field.getAttribute("aria-describedby")?.split(/\s+/) ?? [];
    return ids.map((id) => document.getElementById(id)).find((el) => el?.hasAttribute("data-error"));
  }

  #check(field) {
    const value = field.value.trim();
    const valid = (!field.required || value !== "") && field.checkValidity();
    field.setAttribute("aria-invalid", String(!valid));
    const error = this.#errorFor(field);
    if (error) error.hidden = valid;
    return valid;
  }

  #onSubmit(event, form) {
    if (this.#sending) {
      event.preventDefault();
      return;
    }
    this.#tried = true;
    const invalid = [...form.querySelectorAll("[required]")].filter((field) => !this.#check(field));
    if (invalid.length) {
      event.preventDefault();
      invalid[0].focus();
      return;
    }
    this.#sending = true;
    form.querySelector("[data-submit]")?.setAttribute("aria-busy", "true");
    this.#release = setTimeout(() => this.#done(form), RELEASE_AFTER);
  }
}

if (!customElements.get("validated-form")) customElements.define("validated-form", ValidatedForm);
